import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '..';
import type { Data360ConfigurationItem } from '@server/data360-documentation';
import { Data360ElementType } from '@server/data360-documentation';
import { loadTabsForOrg, saveTabsForOrg, DATA360_TABS_STORAGE_KEY } from '../../lib/documentationTabsLocalStorage';

type ElementsByType = Partial<Record<string, Data360ConfigurationItem[]>>;

export const DATA360_DEFAULT_TAB = Data360ElementType.DATA_MODEL_OBJECT;

export const DATA360_DEFAULT_REMOVABLE_TABS: Data360ElementType[] = [
  Data360ElementType.DATA_LAKE_OBJECT,
  Data360ElementType.DATA_SOURCE,
  Data360ElementType.CALCULATED_INSIGHT,
  Data360ElementType.IDENTITY_RESOLUTION_RULESET,
  Data360ElementType.DATA_SHARE,
  Data360ElementType.DATA_PACKAGE_KIT_DEFINITION,
];

export const DATA360_INITIAL_TABS: Data360ElementType[] = [
  DATA360_DEFAULT_TAB,
  ...DATA360_DEFAULT_REMOVABLE_TABS,
];

export interface Data360DocumentationState {
  elementsByOrg: {
    [crmOrgId: string]: ElementsByType;
  };
  loadingByOrg: {
    [crmOrgId: string]: Partial<Record<string, boolean>>;
  };
  activeParentType: Data360ElementType;
  openTabs: Data360ElementType[];
}

const initialState: Data360DocumentationState = {
  elementsByOrg: {},
  loadingByOrg: {},
  activeParentType: DATA360_DEFAULT_TAB,
  openTabs: [...DATA360_INITIAL_TABS],
};

export const data360DocumentationSlice = createSlice({
  name: 'data360Documentation',
  initialState,
  reducers: {
    setData360Elements: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        tabType: string;
        elements: Data360ConfigurationItem[];
      }>,
    ) => {
      const { crmOrgId, tabType, elements } = action.payload;
      if (!state.elementsByOrg[crmOrgId]) {
        state.elementsByOrg[crmOrgId] = {};
      }
      state.elementsByOrg[crmOrgId][tabType] = elements;
    },

    setData360Loading: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        tabType: string;
        isLoading: boolean;
      }>,
    ) => {
      const { crmOrgId, tabType, isLoading } = action.payload;
      if (!state.loadingByOrg[crmOrgId]) {
        state.loadingByOrg[crmOrgId] = {};
      }
      state.loadingByOrg[crmOrgId][tabType] = isLoading;
    },

    clearData360Elements: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      delete state.elementsByOrg[action.payload.crmOrgId];
      delete state.loadingByOrg[action.payload.crmOrgId];
    },

    setData360ActiveParentType: (state, action: PayloadAction<Data360ElementType>) => {
      state.activeParentType = action.payload;
    },

    loadData360TabsForOrg: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      const stored = loadTabsForOrg<Data360ElementType[]>(DATA360_TABS_STORAGE_KEY, action.payload.crmOrgId);
      if (stored?.length) {
        const withDefaults = [
          DATA360_DEFAULT_TAB,
          ...stored.filter((t) => t !== DATA360_DEFAULT_TAB),
        ];
        state.openTabs = withDefaults;
        state.activeParentType = withDefaults[0];
      }
    },

    addData360Tab: (
      state,
      action: PayloadAction<{ parentType: Data360ElementType; crmOrgId: string }>,
    ) => {
      const { parentType, crmOrgId } = action.payload;
      if (!state.openTabs.includes(parentType)) {
        state.openTabs.push(parentType);
      }
      state.activeParentType = parentType;
      const userTabs = state.openTabs.filter((t) => t !== DATA360_DEFAULT_TAB);
      saveTabsForOrg(DATA360_TABS_STORAGE_KEY, crmOrgId, userTabs);
    },

    removeData360Tab: (
      state,
      action: PayloadAction<{ parentType: Data360ElementType; crmOrgId: string }>,
    ) => {
      const { parentType, crmOrgId } = action.payload;
      if (parentType === DATA360_DEFAULT_TAB) return;
      state.openTabs = state.openTabs.filter((t) => t !== parentType);
      if (state.activeParentType === parentType) {
        state.activeParentType = state.openTabs[state.openTabs.length - 1] ?? DATA360_DEFAULT_TAB;
      }
      const userTabs = state.openTabs.filter((t) => t !== DATA360_DEFAULT_TAB);
      saveTabsForOrg(DATA360_TABS_STORAGE_KEY, crmOrgId, userTabs);
    },
  },
});

export const {
  setData360Elements,
  setData360Loading,
  clearData360Elements,
  setData360ActiveParentType,
  addData360Tab,
  removeData360Tab,
  loadData360TabsForOrg,
} = data360DocumentationSlice.actions;

export const selectData360Elements =
  (crmOrgId: string, tabType: string) => (state: RootState) =>
    state[data360DocumentationSlice.name]?.elementsByOrg[crmOrgId]?.[tabType];

export const selectData360Loading =
  (crmOrgId: string, tabType: string) => (state: RootState) =>
    state[data360DocumentationSlice.name]?.loadingByOrg[crmOrgId]?.[tabType] ?? false;

export const selectAllData360Elements = (crmOrgId: string) => (state: RootState) =>
  state[data360DocumentationSlice.name]?.elementsByOrg[crmOrgId];

export const selectData360ActiveParentType = (state: RootState) =>
  state[data360DocumentationSlice.name]?.activeParentType ?? DATA360_DEFAULT_TAB;

export const selectData360OpenTabs = (state: RootState) =>
  state[data360DocumentationSlice.name]?.openTabs ?? [...DATA360_INITIAL_TABS];

export default data360DocumentationSlice.reducer;
